/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Switch from 'carbon-react/lib/components/switch';
import { FormattedMessage } from 'react-intl';

import { ROUTES } from '@futrli/shared-services';
import { Forecast } from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';

import { useAnimationLatency, useOnboardingUrlInfo } from '../../../onboarding/utils/hooks';
import { ForecastType } from '../../../onboarding/shared/onboarding-type-select';
import {
  BUDGET_ONBOARDING_STEP,
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../../../onboarding/const';
import * as styles from './forecasting-navigation-tabs.scss';
import topArrow from '../../../onboarding/arrows/historical-data-right.png';
import bottomArrow from '../../../onboarding/arrows/historical-data.png';

interface Props {
  showPredictionsExplanation?: boolean;
  togglePredictionsExplanation?: () => void;
  forecast?: Forecast | ConsolidatedForecastListItem | null;
  isConsolidation?: boolean;
}

export const PredictionsExplanation = ({
  showPredictionsExplanation,
  togglePredictionsExplanation,
  forecast,
  isConsolidation
}: Props) => {
  const { pathname } = useLocation();

  const {
    isOnboardingRoute,
    isOnboardingBudgetRoute,
    isOnboardingScenarioRoute,
    isOnboardingScenarioWithInvoicesRoute,
    onboardingStep,
    forecastType
  } = useOnboardingUrlInfo();

  const onboardingStepNum = Number(onboardingStep);

  const showOnboardingArrows =
    (isOnboardingBudgetRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStep === BUDGET_ONBOARDING_STEP.AUTO_PREDICTIONS_EXPLAINED) ||
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_EXPLAINED) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_EXPLAINED);

  const showAnimatedArrow = useAnimationLatency(showOnboardingArrows);

  if (pathname.includes(ROUTES.INVOICES)) {
    return null;
  }

  const showExplanations =
    (isOnboardingBudgetRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum > Number(BUDGET_ONBOARDING_STEP.AUTO_PREDICTIONS_ACTIVITY)) ||
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum > Number(SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_ACTIVITY)) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum > Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_ACTIVITY));

  const showPlaceholder =
    (isOnboardingBudgetRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStep === BUDGET_ONBOARDING_STEP.AUTO_PREDICTIONS_ACTIVITY) ||
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_ACTIVITY) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_ACTIVITY);

  if (isOnboardingRoute && showPlaceholder) {
    return <div style={{ marginLeft: 'auto' }} className={styles.predictionsExplanationsPlaceholder}></div>;
  }

  if (isOnboardingRoute && !showExplanations) {
    return null;
  }

  if (isConsolidation || !(forecast as Forecast)?.include_ai_assumptions) {
    return null;
  }

  return (
    <div style={{ marginLeft: 'auto' }} className={classNames(styles.boldFont, styles.carbonContainer)}>
      {showOnboardingArrows && (
        <img
          src={topArrow}
          style={{ position: 'absolute', bottom: 32, top: -150, zIndex: 11 }}
          alt="onboarding-arrow"
          className={classNames(styles.fadeInContainer, {
            [styles.fadeIn]: showAnimatedArrow
          })}
        />
      )}
      {showOnboardingArrows && (
        <img
          src={bottomArrow}
          style={{ position: 'absolute', right: 60, top: 40, zIndex: 11 }}
          alt="onboarding-arrow"
          className={classNames(styles.fadeInContainer, {
            [styles.fadeIn]: showAnimatedArrow
          })}
        />
      )}
      <Switch
        id="predictions-explanation-switch"
        size="small"
        checked={!!showPredictionsExplanation}
        onChange={togglePredictionsExplanation ?? (() => undefined)}
        mr="12px"
      />
      <label htmlFor="predictions-explanation-switch">
        <FormattedMessage id="predict.predictions_explanation" defaultMessage="Predictions explanation" />
      </label>
    </div>
  );
};
